import { useQuery } from '@apollo/client';
import { gql } from '__generated__';
import {
  ClosingStatusEnumType,
  DealStatusEnumType,
  InvestmentEntityDocumentTypeEnumType,
} from '__generated__/graphql';
import dayjs from 'dayjs';
import { FC } from 'react';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';

import LoadingIndicator from 'primitives/LoadingIndicator';
import HorizontalTimeline from 'primitives/Timeline/HorizontalTimeline';
import VerticalTimeline from 'primitives/Timeline/VerticalTimeline';

import CommitmentStatus from './CommitmentStatus';

const CLOSINGS_QUERY = gql(`
  query getCommitmentsOfUserForDeal($dealId: ID!, $filters: ClosingsFilterType) {
    deal(id: $dealId) {
      id
      status
      finalisedAt
      userClosings(filters: $filters) {
        nodes {
          id
          investmentEntity {
            id
            name
          }
          amount
          status
          fundingSource
          createdAt
          finalisedAt
          wiredAt
          statistics {
            id
            unrealisedValue
            realisedValue
            multipleOnInvestment
          }
          closingDocuments {
            id
            type
            name
            url
          }
          deal {
            companyInformation {
              company {
                name
              }
            }
          }
        }
      }
    }
  }
`);

const Commitments: FC<{ dealId: string }> = ({ dealId }) => {
  const { loading, error, data, refetch } = useQuery(CLOSINGS_QUERY, {
    variables: {
      filters: {
        statuses: [
          ClosingStatusEnumType.Committed,
          ClosingStatusEnumType.Transferred,
          ClosingStatusEnumType.Active,
          ClosingStatusEnumType.Exited,
          ClosingStatusEnumType.PartiallyExited,
        ],
      },
      dealId,
    },
  });

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const commitments = data.deal.userClosings?.nodes || [];
  const dealStatus = data.deal.status;

  const getDealStatusLabel = (dealStatus: DealStatusEnumType) => {
    switch (dealStatus) {
      case DealStatusEnumType.Raising:
        return 'Accepting Commitments';
      case DealStatusEnumType.Pooling:
        return 'Capital Called';
      case DealStatusEnumType.Funded:
        return 'Finalising Paperwork';
      case DealStatusEnumType.Wired:
        return 'Invested';
      case DealStatusEnumType.Exited:
        return 'Exited';
      case DealStatusEnumType.PartiallyExited:
        return 'Partially Exited';
      default:
        return 'Unknown';
    }
  };

  const dealStatusTimeline = [
    {
      label: 'Accepting Commitments',
      date: dayjs(commitments[0].createdAt).format('MMMM DD, YYYY') ?? '–',
      isCurrent: dealStatus === DealStatusEnumType.Raising,
    },
    {
      label: 'Capital Called',
      date: dayjs(commitments[0].createdAt).format('MMMM DD, YYYY') ?? '–',
      isCurrent: dealStatus === DealStatusEnumType.Pooling,
    },
    {
      label: 'Finalising Paperwork',
      date: dayjs(commitments[0].createdAt).format('MMMM DD, YYYY') ?? '–',
      isCurrent: dealStatus === DealStatusEnumType.Funded,
    },
    {
      label: 'Invested',
      date: dayjs(commitments[0].createdAt).format('MMMM DD, YYYY') ?? '–',
      isCurrent: dealStatus === DealStatusEnumType.Wired,
    },
    {
      label: 'Exited',
      date: dayjs(commitments[0].createdAt).format('MMMM DD, YYYY') ?? '–',
      isCurrent: dealStatus === DealStatusEnumType.Exited,
      hide: dealStatus === DealStatusEnumType.PartiallyExited,
    },
    {
      label: 'Partially Exited',
      date: dayjs(commitments[0].createdAt).format('MMMM DD, YYYY') ?? '–',
      isCurrent: dealStatus === DealStatusEnumType.PartiallyExited,
      hide: dealStatus !== DealStatusEnumType.PartiallyExited,
    },
  ];

  return (
    <>
      <div className="custom-bounce">
        <div className="mt-8">
          <div className="mx-auto">
            <div className="mx-auto max-w-2xl space-y-6 sm:px-4 lg:max-w-4xl lg:px-0">
              {commitments.map(commitment => (
                <>
                  <CommitmentStatus
                    key={commitment.id}
                    id={commitment.id}
                    amount={commitment.amount}
                    investmentEntity={commitment.investmentEntity}
                    fundingSource={commitment.fundingSource}
                    createdAt={commitment.createdAt}
                    companyName={commitment.deal.companyInformation.company.name}
                    status={commitment.status}
                    finalisedAt={commitment.finalisedAt}
                    dealStatus={dealStatus}
                  />

                  {/* TIMELINE FOR DEAL STATUS */}

                  <div className="border-gray-200 bg-white rounded sm:rounded-lg border px-4 h-36">
                    <div className="text-sm mt-4 mb-6">
                      Deal Progress:{' '}
                      <span
                        className={
                          dealStatus === DealStatusEnumType.Wired ||
                          dealStatus === DealStatusEnumType.Exited ||
                          dealStatus === DealStatusEnumType.PartiallyExited
                            ? 'text-green-600'
                            : 'text-amber-500'
                        }
                      >
                        {getDealStatusLabel(dealStatus)}
                      </span>
                    </div>
                    {dealStatusTimeline.filter(timeline => timeline?.isCurrent)?.length > 0 && (
                      <div className="flex flex-row w-full justify-between px-8">
                        {dealStatusTimeline
                          .filter(item => !item.hide)
                          .map((timeline, index) => (
                            <HorizontalTimeline
                              key={index}
                              label={timeline.label}
                              date={timeline.date}
                              isCurrent={timeline.isCurrent}
                              isLast={index === dealStatusTimeline.length - 2} // -2 because we show either exited or partially exited
                              isPast={index < dealStatusTimeline.findIndex(t => t.isCurrent)}
                              isFuture={index > dealStatusTimeline.findIndex(t => t.isCurrent)}
                            />
                          ))}
                      </div>
                    )}
                  </div>

                  {/* TIMELINE FOR COMMITMENT STATUS */}

                  <div className="border-gray-200 bg-white rounded sm:rounded-lg border p-4 pb-0">
                    <VerticalTimeline
                      label="Committed"
                      date={commitment.createdAt}
                      status={commitment.createdAt ? 'success' : 'pending'}
                    />
                    <VerticalTimeline
                      label="Capital Call Fulfilled"
                      date={commitment.wiredAt}
                      status={commitment.wiredAt ? 'success' : 'pending'}
                    />
                    <VerticalTimeline
                      label="Invested in Company"
                      date={data.deal.finalisedAt}
                      status={data.deal.finalisedAt ? 'success' : 'pending'}
                      isLast
                    />
                  </div>

                  {commitment.closingDocuments?.filter(
                    doc =>
                      doc.type === InvestmentEntityDocumentTypeEnumType.SchemeInformationDocument
                  ) && (
                    <div>
                      <p className="text-md font-bold mt-4 mb-2">
                        Documents related to this closing
                      </p>
                      <DataTable
                        data={commitment.closingDocuments?.filter(
                          doc =>
                            doc.type ===
                            InvestmentEntityDocumentTypeEnumType.SchemeInformationDocument
                        )}
                        columns={[
                          {
                            label: 'Name',
                            fieldName: 'name',
                          },
                          {
                            label: 'Type',
                            fieldName: 'type',
                            type: 'INVESTMENT_ENTITY_DOCUMENT_TYPE',
                          },
                          {
                            label: 'File',
                            fieldName: 'url',
                            type: 'FILE',
                          },
                        ]}
                      />
                    </div>
                  )}
                </>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Commitments;
